import { ErrorIcon } from '@/shared/ui/assets';
import { CustomTooltip, ErrorMessage } from '@/shared/ui/components';
import classNames from 'classnames';
import _ from 'lodash';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { t } from 'ttag';
import type { MultiIdSearchInputProps } from './multiSearchInput.types';
import './styles.scss';

/**
 * Chip override for the react-select based multi-email field that flags invalid entries. Separates children from the remaining library props, treats a non-empty data.errorMessage as an error, and wraps the chip content in a bottom-placed Tooltip carrying that message with an error icon shown before the label.
 *
 * @param props - React-select multi-value props, including the entry data and its rendered children.
 * @returns Multi-value chip element with tooltip and error icon.
 */
const CustomMultiValue = (props: any) => {
  const { children, ...propsLibrary } = props;
  const isError = !_.isEmpty(propsLibrary.data?.errorMessage);

  return (
    <components.MultiValue {...propsLibrary} className="custom-multi-value-label">
      <CustomTooltip placement="bottom" title={propsLibrary.data?.errorMessage}>
        {isError && <img style={{ marginRight: '6px' }} src={ErrorIcon} alt="Error Icon" />}
        {children}
      </CustomTooltip>
    </components.MultiValue>
  );
};

/**
 * Text input override for the react-select based multi-email field. Renders the library Input while forwarding the onPaste handler taken off selectProps so pasted email lists reach the field's own paste logic.
 *
 * @param props - Props supplied by react-select for the input element.
 * @returns React-select input element with paste forwarding attached.
 */
const CustomInput = ({ ...props }) => (
  <components.Input {...props} onPaste={props.selectProps.onPaste} />
);

const customStyles = {
  container: (provided: any) => ({
    ...provided,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    width: '100%',
    minHeight: '2rem',
    padding: '0 3.5rem 0 1rem',
    borderRadius: '4px',
    border: '1px solid #c6cbd1',
    boxShadow: state.isFocused ? '0px 0px 0px 2px #7fd0d5' : 'none',

    '&:hover': {
      border: '1px solid #c6cbd1',
    },
  }),
  multiValue: (provided: any) => ({
    ...provided,
    alignItems: 'center',
    height: '24px',
    backgroundColor: '#0D9E92',
    borderRadius: '4px',
    color: 'white',
    padding: '0 8px',
    fontSize: '16px',
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    color: 'white',
    paddingLeft: '0 !important',
    fontSize: '12px',
  }),
};

const MultiIdSearchInput = ({
  onChange,
  onInputChange,
  onKeyDown,
  onPaste,
  onBlur,
  inputValue,
  className,
  title,
  placeholder,
  isError = false,
  errorMessage = '',
  value,
  classNamePrefix,
}: MultiIdSearchInputProps) => (
  <div className={classNames('custom-multi-id-input-v2 justify-end', className)}>
    <div className="title-name">{title}</div>
    <div style={{ height: '100%', width: '100%' }}>
      <CreatableSelect
        className="custom-multi-id-input__input"
        components={{
          DropdownIndicator: (): any => null,
          MultiValue: CustomMultiValue,
          Input: CustomInput,
        }}
        inputValue={inputValue}
        isMulti
        menuIsOpen={false}
        onChange={onChange}
        onInputChange={onInputChange}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        onPaste={onPaste}
        value={value}
        styles={customStyles}
        onBlur={onBlur}
        classNamePrefix={classNamePrefix}
        aria-label={typeof title === 'string' ? title : t`Study ID`}
      />
    </div>
    {isError && !!errorMessage && <ErrorMessage errorMessage={errorMessage} hasIcon />}
  </div>
);

export default MultiIdSearchInput;
